/**
 * Admin V3 — Booking cancel modal (AV3-M1)
 *
 * Operator confirms cancellation of a booking via
 * POST /admin/control-tower/bookings/{id}/cancel (RBAC: admin:bookings:cancel).
 * Parity with the legacy V2 BookingDetailPage cancel flow: typed reason +
 * optional free-text detail + customer/driver notification toggles.
 *
 * No new business logic — the existing backend behaviour is reused verbatim.
 */
import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const BookingCancelModal = ({ open, bookingId, booking, reasons = [], onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation(`/admin/control-tower/bookings/${bookingId || '_'}/cancel`, 'post');
  const { showSuccess, showError } = useToast();

  const [reasonType, setReasonType] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [notifyDriver, setNotifyDriver] = useState(true);

  if (!open || !bookingId) return null;

  const valid = Boolean(reasonType);

  const submit = () => {
    m.mutate(
      {
        reason_type: reasonType,
        reason_detail: reasonDetail || null,
        notify_customer: notifyCustomer,
        notify_driver: notifyDriver,
      },
      {
        onSuccess: () => {
          showSuccess(t('bookings.cancel.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('bookings.cancel.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-booking-cancel-modal">
      <div className="w-[520px] max-w-[94vw] rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-slate-900">{t('bookings.cancel.title', locale)}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-booking-cancel-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-4 text-[12px] text-slate-500">
          {t('bookings.cancel.description', locale).replace('{ref}', booking?.booking_reference || '')}
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.cancel.reasonLabel', locale)}</label>
            <select
              value={reasonType}
              onChange={(e) => setReasonType(e.target.value)}
              className={inputCls}
              data-testid="adminv3-booking-cancel-reason"
            >
              <option value="">{t('bookings.cancel.reasonPlaceholder', locale)}</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-semibold text-slate-600">{t('bookings.cancel.detailLabel', locale)}</label>
            <textarea
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              rows={2}
              className={inputCls}
              data-testid="adminv3-booking-cancel-detail"
            />
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 text-[13px] text-slate-700">
              <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} data-testid="adminv3-booking-cancel-notify-customer" />
              {t('bookings.cancel.notifyCustomer', locale)}
            </label>
            <label className="flex items-center gap-2 text-[13px] text-slate-700">
              <input type="checkbox" checked={notifyDriver} onChange={(e) => setNotifyDriver(e.target.checked)} data-testid="adminv3-booking-cancel-notify-driver" />
              {t('bookings.cancel.notifyDriver', locale)}
            </label>
          </div>
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-booking-cancel-missing">
            {t('bookings.cancel.reasonRequired', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-booking-cancel-dismiss">
            {t('bookings.cancel.cancel', locale)}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={submit}
            disabled={!valid || m.loading}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="adminv3-booking-cancel-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('bookings.cancel.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingCancelModal;
